<template>
  <div class="system-config">
    <h3>系统配置</h3>
    <el-form v-loading="loading" label-width="140px" style="max-width: 720px">
      <el-form-item label="storage.type">
        <el-input v-model="form.storageType" />
      </el-form-item>
      <el-form-item label="切片时长选项">
        <el-select v-model="form.allowedDurations" multiple style="width: 100%">
          <el-option v-for="d in allDurations" :key="d" :label="`${d}s`" :value="d" />
        </el-select>
      </el-form-item>
      <el-form-item label="Worker 并发">
        <el-input-number v-model="form.maxConcurrency" :min="1" :max="8" />
      </el-form-item>
      <el-form-item label="视频保留时长">
        <el-input :value="`${form.videoRetentionHours} 小时`" disabled />
        <div class="form-hint">到期后自动清理存储文件，任务记录保留。修改请编辑 application.yml 中 aivideoclip.cleanup.video-retention-hours</div>
      </el-form-item>
      <el-divider />
      <h4 class="section-title">功能开关（用户端）</h4>
      <el-form-item label="固定时长切片">
        <el-switch v-model="form.featureFixedDurationSegment" />
        <div class="form-hint">
          默认关闭。开启后 Web 显示「固定时长切片」菜单，且允许提交固定时长/按数量切片 API；关闭时菜单隐藏且 API 返回功能未开放。
        </div>
      </el-form-item>
      <el-form-item label="精彩峰段 ASR">
        <div class="asr-switch-row">
          <el-switch
            v-model="form.featureHighlightAsr"
            :disabled="!asrConfigured"
          />
          <el-button
            type="text"
            size="mini"
            :loading="rechecking"
            @click="recheckAsr"
          >重新检测</el-button>
        </div>
        <div class="form-hint">
          适用于游戏 / 唱歌 / 舞蹈 / 其他。对精彩候选峰段做短音频识别并加权重排。
          与后端联动：未配置 Appkey/AccessKey 时强制关闭；配置齐全时默认开启。
          欠费、试用耗尽或识别失败时自动跳过，仍走原有声音/切镜逻辑。
        </div>
        <div v-if="asrStatusHint" class="form-status" :class="asrStatusClass">
          {{ asrStatusHint }}
        </div>
        <div v-if="asrDisabledReason" class="form-warn">
          {{ asrDisabledReason }}
        </div>
      </el-form-item>
      <el-divider />
      <h4 class="section-title">测试上传（不对用户端开放）</h4>
      <el-form-item label="启用测试硬顶">
        <el-switch v-model="form.testUploadEnabled" />
        <div class="form-hint">开启后环境硬顶使用下方配置，定价页与公开档位不变</div>
      </el-form-item>
      <el-form-item label="测试上传上限">
        <el-input-number
          v-model="form.testUploadMaxSizeMb"
          :min="1024"
          :max="51200"
          :step="1024"
          :disabled="!form.testUploadEnabled"
        />
        <span class="unit-suffix">MB（默认 20480 = 20GB）</span>
        <div class="form-hint">
          仅用于内部联调：在「用户管理」为测试账号设置「单文件 override」后生效；勿对普通用户开放
        </div>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="save">保存</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { fetchAdminConfig, updateAdminConfig, recheckHighlightAsr } from '../api/admin';

export default {
  name: 'SystemConfig',
  data() {
    return {
      loading: false,
      rechecking: false,
      allDurations: [30, 60, 90, 180, 270, 360],
      asrConfigured: false,
      asrEffective: false,
      asrStatusCode: '',
      asrDisabledReason: '',
      form: {
        storageType: 'local',
        allowedDurations: [30, 60, 90, 180, 270, 360],
        maxConcurrency: 2,
        videoRetentionHours: 24,
        featureFixedDurationSegment: false,
        featureHighlightAsr: false,
        testUploadEnabled: false,
        testUploadMaxSizeMb: 20480,
        highlightCategoryWeights: null,
      },
    };
  },
  computed: {
    asrStatusHint() {
      const code = this.asrStatusCode;
      if (code === 'OK' && this.asrEffective) {
        return '状态：可用（后端凭证已配置，开关已开启）';
      }
      if (code === 'NOT_CONFIGURED') {
        return '状态：不可用（后端未配置凭证）';
      }
      if (code === 'BILLING_BLOCKED') {
        return '状态：已封锁（费用/试用/配额问题，分析任务将跳过 ASR）';
      }
      if (code === 'CREDENTIAL_ERROR') {
        return '状态：凭证异常';
      }
      if (code === 'ADMIN_DISABLED') {
        return '状态：已手动关闭';
      }
      return code ? `状态：${code}` : '';
    },
    asrStatusClass() {
      if (this.asrEffective) return 'is-ok';
      if (this.asrStatusCode === 'ADMIN_DISABLED') return 'is-muted';
      return 'is-bad';
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    applyAsrStatus(data) {
      this.asrConfigured = !!data.highlightAsrConfigured;
      this.asrEffective = !!data.highlightAsrEffective;
      this.asrStatusCode = data.highlightAsrStatusCode || '';
      this.asrDisabledReason = data.highlightAsrDisabledReason || '';
      // 开关展示：跟后端联动后的值（未配置时后端会强制 false）
      this.form.featureHighlightAsr = !!data.featureHighlightAsr;
    },
    async load() {
      this.loading = true;
      try {
        const data = await fetchAdminConfig();
        this.form.storageType = data.storageType || 'local';
        this.form.allowedDurations = data.allowedDurations || this.allDurations;
        this.form.maxConcurrency = data.maxConcurrency || 2;
        this.form.videoRetentionHours = data.videoRetentionHours || 24;
        this.form.featureFixedDurationSegment = !!data.featureFixedDurationSegment;
        this.form.testUploadEnabled = !!data.testUploadEnabled;
        this.form.testUploadMaxSizeMb = data.testUploadMaxSizeMb || 20480;
        this.form.highlightCategoryWeights = data.highlightCategoryWeights || null;
        this.applyAsrStatus(data);
      } catch (e) {
        this.$message.error(e.message || '加载配置失败');
      } finally {
        this.loading = false;
      }
    },
    async recheckAsr() {
      this.rechecking = true;
      try {
        const data = await recheckHighlightAsr();
        this.applyAsrStatus(data);
        if (data.highlightAsrEffective) {
          this.$message.success('峰段 ASR 检测通过');
        } else {
          this.$message.warning(data.highlightAsrDisabledReason || '峰段 ASR 仍不可用');
        }
      } catch (e) {
        this.$message.error(e.message || '重新检测失败');
      } finally {
        this.rechecking = false;
      }
    },
    async save() {
      try {
        if (!this.asrConfigured && this.form.featureHighlightAsr) {
          this.$message.error('后端未配置 ASR 凭证，无法开启');
          this.form.featureHighlightAsr = false;
          return;
        }
        const data = await updateAdminConfig(this.form);
        this.applyAsrStatus(data);
        this.$message.success('配置已保存');
      } catch (e) {
        this.$message.error(e.message || '保存失败');
      }
    },
  },
};
</script>

<style scoped>
.form-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
.form-warn {
  margin-top: 8px;
  font-size: 13px;
  color: #dc2626;
  line-height: 1.55;
  font-weight: 500;
}
.form-status {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.5;
  font-weight: 500;
}
.form-status.is-ok { color: #059669; }
.form-status.is-bad { color: #dc2626; }
.form-status.is-muted { color: #b45309; }
.asr-switch-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.section-title {
  margin: 0 0 12px;
  font-size: 15px;
  color: #111827;
}
.unit-suffix {
  margin-left: 8px;
  color: #6b7280;
  font-size: 13px;
}
</style>
